import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useIsDesktop } from "@/hooks/use-media-query";
import { Breadcrumb, enabledModules } from "@/components/AppShell";
import { EmptyState } from "@/components/EmptyState";
import { ConfidencePill } from "@/components/ConfidencePill";
import { StatusBadge } from "@/components/StatusBadge";
import { AttachmentViewer } from "@/components/bilag/AttachmentViewer";
import { DocTypeBadge, AttachmentStatusBadge } from "@/components/bilag/AttachmentBadges";
import { LinkPickerDialog } from "@/components/bilag/LinkPickerDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from "@/components/ui/resizable";
import { Skeleton } from "@/components/ui/skeleton";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { DOC_TYPES, DOC_TYPE_OPTIONS, VAT_RATES } from "@/lib/attachmentConstants";
import { SALES_STATUS } from "@/lib/salesConstants";
import { formatKr, formatDate, formatDateTime, formatMonth, formatBytes } from "@/lib/format";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, Pencil, Save, X, Trash2, Loader2, Sparkles, Info, Link2, Unlink, FileText, Receipt, ExternalLink, RotateCcw, ChevronDown, Undo2, Archive, ScanText } from "lucide-react";
import { cn } from "@/lib/utils";

const METHOD_LABELS = {
  pdf_text: "Tekst-PDF lest med AI",
  vision_scanned_pdf: "Skannet PDF lest som bilde med AI",
  vision: "Bilde lest med AI",
  ehf: "EHF / elektronisk faktura (lest uten AI)",
};

const FieldRow = ({ label, value, confidence, mono, testId }) => (
  <div className="flex items-start justify-between gap-3 py-2.5 border-b last:border-b-0">
    <div className="flex items-center gap-2 text-sm text-muted-foreground shrink-0">{label}<ConfidencePill value={confidence} /></div>
    <div className={cn("text-sm font-medium text-right min-w-0 break-words", mono && "tnum")} data-testid={testId}>{value || <span className="text-muted-foreground italic font-normal">Mangler</span>}</div>
  </div>
);

const EditField = ({ id, label, confidence, children, hint, className }) => (
  <div className={cn("space-y-1.5", className)}>
    <div className="flex items-center gap-2"><Label htmlFor={id}>{label}</Label><ConfidencePill value={confidence} /></div>
    {children}
    {hint ? <p className="text-xs text-muted-foreground">{hint}</p> : null}
  </div>
);

const toForm = (a) => {
  const f = a?.fields || {};
  return {
    doc_type: a?.doc_type || "",
    date: f.date || "",
    counterparty: f.counterparty || "",
    amount: f.amount != null ? Number(f.amount).toFixed(2).replace(".", ",") : "",
    currency: f.currency || "NOK",
    vat_amount: f.vat_amount != null ? Number(f.vat_amount).toFixed(2).replace(".", ",") : "",
    vat_rate: f.vat_rate != null ? String(Number(f.vat_rate)) : "ingen",
    voucher_no: f.voucher_no || "",
    period: f.period || "",
    comment: f.comment || "",
  };
};

const num = (v) => {
  if (v === "" || v === null || v === undefined) return null;
  const n = Number(String(v).replace(/\s/g, "").replace(",", "."));
  return Number.isNaN(n) ? String(v) : n;
};

const toPayload = (form) => ({
  doc_type: form.doc_type || null,
  fields: {
    date: form.date || null,
    counterparty: form.counterparty.trim() || null,
    amount: num(form.amount),
    currency: (form.currency || "NOK").trim().toUpperCase() || "NOK",
    vat_amount: num(form.vat_amount),
    vat_rate: form.vat_rate === "ingen" ? null : Number(form.vat_rate),
    voucher_no: form.voucher_no.trim() || null,
    period: form.period || null,
    comment: form.comment.trim() || null,
  },
});

export default function AttachmentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { canWrite, isOwner, actAs, activeWorkspace } = useAuth();
  const isDesktop = useIsDesktop();
  const fakturaOn = isOwner && enabledModules(activeWorkspace).includes("faktura");
  const [att, setAtt] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(toForm(null));
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [picker, setPicker] = useState(null); // "sales" | "invoice" | null
  const [rawOpen, setRawOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/attachments/${id}`);
      setAtt(res.data);
      setForm(toForm(res.data));
    } catch (e) {
      setError(errorMessage(e, "Fant ikke bilaget"));
    }
  }, [id]);

  useEffect(() => { setAtt(null); setError(""); setEditing(false); load(); }, [load]);

  const apply = (data) => { if (data) { setAtt((prev) => ({ ...(prev || {}), ...data })); setForm(toForm(data)); } };

  const run = async (key, fn, successMsg) => {
    setBusy(key);
    try {
      const res = await fn();
      if (successMsg) toast.success(successMsg);
      return res;
    } catch (e) {
      toast.error(errorMessage(e));
      return null;
    } finally {
      setBusy("");
    }
  };

  const save = async (confirm = false) => {
    if (confirm && !form.doc_type) { toast.error("Velg bilagstype før du bekrefter"); setEditing(true); return false; }
    setSaving(true);
    try {
      const res = await api.put(`/attachments/${id}`, { ...toPayload(form), confirm });
      apply(res.data);
      setEditing(false);
      toast.success(confirm ? "Bilaget er bekreftet" : "Endringer lagret");
      return true;
    } catch (e) {
      toast.error(errorMessage(e, "Kunne ikke lagre"));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const analyze = async () => {
    const res = await run("analyze", () => api.post(`/attachments/${id}/analyze`), null);
    if (res) {
      apply(res.data);
      const ex = res.data.extraction || {};
      if (ex.error) toast.warning("Automatisk lesing mislyktes", { description: "Fyll inn egenskapene manuelt." });
      else toast.success("Bilaget er lest", { description: res.data.doc_type_label ? `Foreslått type: ${res.data.doc_type_label}` : undefined });
    }
  };

  const createInvoice = async () => {
    const res = await run("create", () => api.post(`/attachments/${id}/create-invoice`), null);
    if (res) {
      apply(res.data.attachment);
      window.dispatchEvent(new Event("rp:refresh-notifications"));
      toast.success("Regning opprettet i Regninger", { description: res.data.invoice?.status === "ma_kontrolleres" ? "Kontroller opplysningene før betaling." : undefined, action: { label: "Åpne regningen", onClick: () => navigate(`/fakturaer/${res.data.invoice.id}`) } });
    }
  };

  const link = async (kind, item) => {
    const res = await run("link", () => (kind === "sales" ? api.post(`/attachments/${id}/link-sales-invoice`, { sales_invoice_id: item.id }) : api.post(`/attachments/${id}/link-invoice`, { invoice_id: item.id })), kind === "sales" ? `Koblet til faktura ${item.number_display || ""}`.trim() : `Koblet til regningen fra ${item.issuer_name || "leverandør"}`);
    if (res) { apply(res.data); setPicker(null); }
  };

  const unlink = async (kind) => {
    const res = await run(`unlink-${kind}`, () => api.delete(`/attachments/${id}/links/${kind}`), "Koblingen er fjernet");
    if (res) apply(res.data);
  };

  const remove = async () => {
    try {
      await api.delete(`/attachments/${id}`);
      toast.success("Bilaget er slettet");
      navigate("/bilag");
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };

  const restoreSuggestion = () => {
    if (!att?.suggested) return;
    const s = att.suggested;
    setForm(toForm({ doc_type: s.doc_type, fields: s }));
    toast("AI-forslaget er lagt inn – lagre for å beholde det");
  };

  const conf = useMemo(() => att?.extraction?.field_confidence || {}, [att]);

  if (error) return <EmptyState icon={AlertTriangle} title="Fant ikke bilaget" description={error} actionLabel="Til bilagsbanken" actionTo="/bilag" testId="bilag-detail-error" />;
  if (!att) {
    return (
      <div className="space-y-4" data-testid="bilag-detail-loading">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="h-12 w-96" />
        <div className="grid lg:grid-cols-[3fr_2fr] gap-4"><Skeleton className="h-[520px]" /><Skeleton className="h-[520px]" /></div>
      </div>
    );
  }

  const f = att.fields || {};
  const ex = att.extraction || {};
  const isConfirmed = att.status === "bekreftet";
  const isNew = att.status === "ny";
  const linkedInvoice = att.linked?.invoice;
  const linkedSales = att.linked?.sales_invoice;
  const isOutgoing = att.doc_type === "utgaende_faktura";
  const isIncoming = att.doc_type === "inngaende_regning";
  const title = f.counterparty || att.filename;
  const subtitle = [f.date ? formatDate(f.date) : null, f.voucher_no ? `Nr ${f.voucher_no}` : null, f.period ? formatMonth(f.period) : null].filter(Boolean).join(" · ");

  const DetailsPanel = (
    <div className="space-y-4">
      {!canWrite ? (
        <Alert className="actas-banner border" data-testid="bilag-readonly-alert">
          <Info className="h-4 w-4" />
          <AlertTitle>Kun innsyn</AlertTitle>
          <AlertDescription>Du kan se dette bilaget for {actAs?.owner_name}, men ikke endre, bekrefte eller koble det.</AlertDescription>
        </Alert>
      ) : null}

      {ex.error ? (
        <Alert className="status-forfaller_snart border" data-testid="bilag-extraction-error">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between gap-2">Automatisk lesing mislyktes{canWrite ? <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={analyze} disabled={!!busy} data-testid="bilag-analyze-retry">{busy === "analyze" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <RotateCcw className="h-3.5 w-3.5 mr-1" />} Prøv igjen</Button> : null}</AlertTitle>
          <AlertDescription>Vi klarte ikke å lese dokumentet. Fyll inn egenskapene manuelt, eller prøv å lese på nytt.</AlertDescription>
        </Alert>
      ) : isNew ? (
        <Alert className="bg-accent border-transparent" data-testid="bilag-not-analyzed">
          <ScanText className="h-4 w-4" />
          <AlertTitle className="flex items-center justify-between gap-2">Ikke lest ennå{canWrite ? <Button size="sm" className="h-7 text-xs" onClick={analyze} disabled={!!busy} data-testid="bilag-analyze-button">{busy === "analyze" ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Sparkles className="h-3.5 w-3.5 mr-1" />} Les med AI</Button> : null}</AlertTitle>
          <AlertDescription>La AI foreslå type, motpart, beløp og periode – eller fyll inn egenskapene selv.</AlertDescription>
        </Alert>
      ) : !isConfirmed ? (
        <Alert className="status-ma_kontrolleres border" data-testid="bilag-suggested-alert">
          <Sparkles className="h-4 w-4" />
          <AlertTitle>Kontroller forslaget</AlertTitle>
          <AlertDescription>
            {ex.doc_type_reason ? <span>{ex.doc_type_reason}. </span> : null}
            {ex.overall_confidence != null ? `Samlet sikkerhet ${Math.round(ex.overall_confidence * 100)} %. ` : ""}
            Sjekk feltene mot dokumentet, rett opp og bekreft.
          </AlertDescription>
        </Alert>
      ) : null}

      {/* Kobling */}
      <Card data-testid="bilag-link-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><Link2 className="h-4 w-4 text-primary" /> Kobling</CardTitle>
          <CardDescription>
            {linkedInvoice || linkedSales ? "Bilaget er koblet til et dokument i Controlle." : isOutgoing ? "Koble bilaget til fakturaen du har sendt fra Faktura-modulen." : isIncoming ? "Opprett regningen i Regninger med ett klikk – eller koble til en regning som allerede finnes." : "Koble bilaget til en regning eller faktura når det hører til en betaling."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {linkedInvoice ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border bg-secondary px-3 py-2" data-testid="bilag-linked-invoice">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-card border flex items-center justify-center shrink-0"><FileText className="h-4 w-4 text-primary" /></div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">Regning i Regninger</p>
                  <p className="font-medium truncate">{linkedInvoice.issuer_name || "Ukjent leverandør"} <span className="tnum text-muted-foreground font-normal">· {formatKr(linkedInvoice.amount, linkedInvoice.currency)}</span></p>
                  <div className="mt-1"><StatusBadge status={linkedInvoice.status} /></div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <Button variant="outline" size="sm" asChild><Link to={`/fakturaer/${linkedInvoice.id}`} data-testid="bilag-open-invoice"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Åpne</Link></Button>
                {canWrite ? <Button variant="ghost" size="sm" onClick={() => unlink("invoice")} disabled={!!busy} aria-label="Fjern kobling" data-testid="bilag-unlink-invoice"><Unlink className="h-4 w-4" /></Button> : null}
              </div>
            </div>
          ) : null}
          {linkedSales ? (
            <div className="flex items-center justify-between gap-2 rounded-lg border bg-secondary px-3 py-2" data-testid="bilag-linked-sales">
              <div className="flex items-center gap-3 min-w-0">
                <div className="h-9 w-9 rounded-lg bg-card border flex items-center justify-center shrink-0"><Receipt className="h-4 w-4 text-primary" /></div>
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">{linkedSales.type === "kreditnota" ? "Kreditnota" : "Faktura"} i Faktura-modulen</p>
                  <p className="font-medium truncate">{linkedSales.number_display || "(kladd)"}{linkedSales.customer_name ? ` · ${linkedSales.customer_name}` : ""} <span className="tnum text-muted-foreground font-normal">· {formatKr(linkedSales.total, linkedSales.currency)}</span></p>
                  {SALES_STATUS[linkedSales.status] ? <span className={cn("mt-1 inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium", SALES_STATUS[linkedSales.status].cls)}>{SALES_STATUS[linkedSales.status].label}</span> : null}
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {isOwner ? <Button variant="outline" size="sm" asChild><Link to={`/faktura/fakturaer/${linkedSales.id}`} data-testid="bilag-open-sales"><ExternalLink className="h-3.5 w-3.5 mr-1" /> Åpne</Link></Button> : null}
                {canWrite ? <Button variant="ghost" size="sm" onClick={() => unlink("sales-invoice")} disabled={!!busy} aria-label="Fjern kobling" data-testid="bilag-unlink-sales"><Unlink className="h-4 w-4" /></Button> : null}
              </div>
            </div>
          ) : null}

          {canWrite ? (
            <div className="flex flex-wrap gap-2 pt-1">
              {!linkedInvoice && !isOutgoing ? (
                <>
                  <Button onClick={createInvoice} disabled={!!busy} data-testid="bilag-create-invoice">{busy === "create" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />} Opprett regning i Regninger</Button>
                  <Button variant="outline" onClick={() => setPicker("invoice")} disabled={!!busy} data-testid="bilag-link-invoice"><Link2 className="h-4 w-4 mr-2" /> Koble til eksisterende regning</Button>
                </>
              ) : null}
              {!linkedSales && fakturaOn && !isIncoming ? (
                <Button variant={isOutgoing ? "default" : "outline"} onClick={() => setPicker("sales")} disabled={!!busy} data-testid="bilag-link-sales"><Receipt className="h-4 w-4 mr-2" /> Koble til faktura</Button>
              ) : null}
            </div>
          ) : null}
          {canWrite && isOutgoing && !fakturaOn ? <p className="text-xs text-muted-foreground">Faktura-modulen er ikke aktivert for denne virksomheten. Slå den på under Virksomheter for å koble utgående fakturaer.</p> : null}
        </CardContent>
      </Card>

      {/* Egenskaper */}
      <Card data-testid="bilag-fields-card">
        <CardHeader className="pb-3 flex flex-row items-start justify-between space-y-0 gap-2">
          <div>
            <CardTitle className="text-base">Egenskaper</CardTitle>
            <CardDescription>{METHOD_LABELS[ex.method] || (att.analyzed_at ? "Lest automatisk" : "Ikke lest automatisk")}{att.analyzed_at ? ` · ${formatDateTime(att.analyzed_at)}` : ""}</CardDescription>
          </div>
          {canWrite ? (editing ? (
            <div className="flex gap-1 shrink-0">
              <Button variant="ghost" size="sm" onClick={() => { setEditing(false); setForm(toForm(att)); }} data-testid="bilag-cancel-edit"><X className="h-4 w-4 mr-1" /> Avbryt</Button>
              <Button size="sm" onClick={() => save(false)} disabled={saving} data-testid="bilag-save">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Save className="h-4 w-4 mr-1" />} Lagre</Button>
            </div>
          ) : (
            <div className="flex gap-1 shrink-0">
              {!isNew ? <Button variant="ghost" size="sm" onClick={analyze} disabled={!!busy} aria-label="Les på nytt med AI" data-testid="bilag-reanalyze">{busy === "analyze" ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />}<span className="ml-1 hidden sm:inline">Les på nytt</span></Button> : null}
              <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="bilag-edit"><Pencil className="h-4 w-4 mr-1" /> Rediger</Button>
            </div>
          )) : null}
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <EditField id="doc_type" label="Bilagstype" confidence={conf.doc_type} className="sm:col-span-2" hint={form.doc_type ? DOC_TYPES[form.doc_type]?.help : "Velg om dette er noe du skal betale, noe du har fakturert, eller et annet bilag."}>
                <Select value={form.doc_type || "velg"} onValueChange={(v) => setForm({ ...form, doc_type: v === "velg" ? "" : v })}>
                  <SelectTrigger id="doc_type" data-testid="bilag-edit-doctype"><SelectValue placeholder="Velg bilagstype" /></SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="velg" disabled>Velg bilagstype</SelectItem>
                    {DOC_TYPE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`bilag-doctype-option-${o.value}`}><span className="inline-flex items-center gap-2"><o.icon className="h-3.5 w-3.5" /> {o.label}</span></SelectItem>)}
                  </SelectContent>
                </Select>
              </EditField>
              <EditField id="date" label="Bilagsdato" confidence={conf.date}><Input id="date" type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} data-testid="bilag-edit-date" /></EditField>
              <EditField id="counterparty" label="Motpart" confidence={conf.counterparty} hint={form.doc_type === "utgaende_faktura" ? "Kunden du fakturerte" : "Leverandør / avsender"}><Input id="counterparty" value={form.counterparty} onChange={(e) => setForm({ ...form, counterparty: e.target.value })} data-testid="bilag-edit-counterparty" /></EditField>
              <EditField id="amount" label="Beløp inkl. mva" confidence={conf.amount}>
                <div className="flex gap-2">
                  <Input id="amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} inputMode="decimal" className="tnum" placeholder="0,00" data-testid="bilag-edit-amount" />
                  <Input value={form.currency} onChange={(e) => setForm({ ...form, currency: e.target.value })} maxLength={3} className="w-20 uppercase" aria-label="Valuta" data-testid="bilag-edit-currency" />
                </div>
              </EditField>
              <EditField id="vat_amount" label="MVA-beløp" confidence={conf.vat_amount}><Input id="vat_amount" value={form.vat_amount} onChange={(e) => setForm({ ...form, vat_amount: e.target.value })} inputMode="decimal" className="tnum" placeholder="0,00" data-testid="bilag-edit-vat-amount" /></EditField>
              <EditField id="vat_rate" label="MVA-sats">
                <Select value={form.vat_rate} onValueChange={(v) => setForm({ ...form, vat_rate: v })}>
                  <SelectTrigger id="vat_rate" data-testid="bilag-edit-vat-rate"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-card">
                    <SelectItem value="ingen">Ikke oppgitt</SelectItem>
                    {VAT_RATES.map((r) => <SelectItem key={r} value={String(r)}>{r} %</SelectItem>)}
                  </SelectContent>
                </Select>
              </EditField>
              <EditField id="voucher_no" label="Bilagsnummer" confidence={conf.voucher_no} hint="Faktura-, kvitterings- eller bilagsnummer"><Input id="voucher_no" value={form.voucher_no} onChange={(e) => setForm({ ...form, voucher_no: e.target.value })} className="tnum" data-testid="bilag-edit-voucher" /></EditField>
              <EditField id="period" label="Periode" hint={form.period ? `Regnskapsår ${form.period.slice(0, 4)}` : "Utledes fra bilagsdato hvis tom"}><Input id="period" type="month" value={form.period} onChange={(e) => setForm({ ...form, period: e.target.value })} data-testid="bilag-edit-period" /></EditField>
              <EditField id="comment" label="Kommentar" className="sm:col-span-2"><Textarea id="comment" rows={2} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} placeholder="Hva gjelder bilaget?" data-testid="bilag-edit-comment" /></EditField>
              {att.suggested ? <div className="sm:col-span-2"><Button variant="ghost" size="sm" onClick={restoreSuggestion} data-testid="bilag-restore-suggestion"><Undo2 className="h-4 w-4 mr-1" /> Tilbake til AI-forslaget</Button></div> : null}
            </div>
          ) : (
            <div>
              <FieldRow label="Bilagstype" value={att.doc_type ? <DocTypeBadge docType={att.doc_type} /> : null} confidence={conf.doc_type} testId="bilag-field-doctype" />
              <FieldRow label="Bilagsdato" value={f.date ? formatDate(f.date) : null} confidence={conf.date} mono testId="bilag-field-date" />
              <FieldRow label="Motpart" value={f.counterparty} confidence={conf.counterparty} testId="bilag-field-counterparty" />
              <FieldRow label="Beløp inkl. mva" value={f.amount != null ? formatKr(f.amount, f.currency) : null} confidence={conf.amount} mono testId="bilag-field-amount" />
              <FieldRow label="MVA" value={f.vat_amount != null ? `${formatKr(f.vat_amount, f.currency)}${f.vat_rate != null ? ` (${Number(f.vat_rate)} %)` : ""}` : f.vat_rate != null ? `${Number(f.vat_rate)} %` : null} confidence={conf.vat_amount} mono testId="bilag-field-vat" />
              <FieldRow label="Bilagsnummer" value={f.voucher_no} confidence={conf.voucher_no} mono testId="bilag-field-voucher" />
              <FieldRow label="År / periode" value={f.period ? `${f.year} · ${formatMonth(f.period)}` : f.year ? String(f.year) : null} mono testId="bilag-field-period" />
              <FieldRow label="Kommentar" value={f.comment} testId="bilag-field-comment" />
            </div>
          )}
          {!isConfirmed && canWrite ? (
            <div className="mt-5 rounded-lg border bg-accent p-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="bilag-confirm-bar">
              <p className="text-sm text-accent-foreground">{att.doc_type ? "Stemmer egenskapene med dokumentet? Bekreft for å arkivere bilaget." : "Velg bilagstype (rediger) før du kan bekrefte."}</p>
              <Button onClick={() => save(true)} disabled={saving || !!busy || !form.doc_type} data-testid="bilag-confirm">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />} Bekreft{editing ? " og lagre" : ""}</Button>
            </div>
          ) : null}
          {isConfirmed ? <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-success" /> Bekreftet {formatDateTime(att.confirmed_at)}</p> : null}
        </CardContent>
      </Card>

      {/* Utlest tekst */}
      {att.raw_text_preview ? (
        <Collapsible open={rawOpen} onOpenChange={setRawOpen}>
          <Card>
            <CollapsibleTrigger asChild>
              <button type="button" className="w-full text-left px-6 py-4 flex items-center justify-between gap-2 hover:bg-secondary/60 rounded-xl transition-colors" aria-expanded={rawOpen} data-testid="bilag-rawtext-toggle">
                <span className="text-base font-semibold flex items-center gap-2"><ScanText className="h-4 w-4 text-primary" /> Utlest tekst</span>
                <ChevronDown className={cn("h-4 w-4 text-muted-foreground transition-transform", rawOpen && "rotate-180")} />
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <CardContent className="pt-0"><pre className="text-xs leading-5 whitespace-pre-wrap break-words rounded-lg bg-secondary p-3 max-h-72 overflow-auto" data-testid="bilag-rawtext">{att.raw_text_preview}</pre></CardContent>
            </CollapsibleContent>
          </Card>
        </Collapsible>
      ) : null}

      {/* Fil */}
      <Card data-testid="bilag-file-card">
        <CardHeader className="pb-3"><CardTitle className="text-base flex items-center gap-2"><Archive className="h-4 w-4 text-primary" /> Fil</CardTitle></CardHeader>
        <CardContent>
          <FieldRow label="Filnavn" value={att.filename} />
          {att.source_path && att.source_path !== att.filename ? <FieldRow label="Fra mappe" value={att.source_path} /> : null}
          <FieldRow label="Størrelse" value={att.size ? formatBytes(att.size) : null} mono />
          <FieldRow label="Lagt inn" value={formatDateTime(att.created_at)} mono />
          <FieldRow label="Fingeravtrykk" value={att.sha256 ? <span title={att.sha256}>{att.sha256.slice(0, 16)}…</span> : null} mono />
        </CardContent>
      </Card>

      {isOwner ? (
        <div className="flex justify-end">
          <Button variant="ghost" className="text-destructive hover:text-destructive" onClick={() => setDeleteOpen(true)} data-testid="bilag-delete"><Trash2 className="h-4 w-4 mr-2" /> Slett bilag</Button>
        </div>
      ) : null}
    </div>
  );

  return (
    <div className="space-y-5">
      <Breadcrumb items={[{ label: "Bilagsbank", to: "/bilag" }, { label: title }]} />

      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <DocTypeBadge docType={att.doc_type} size="md" />
            <AttachmentStatusBadge status={att.status} size="md" />
          </div>
          <h1 className="text-2xl md:text-3xl font-semibold tracking-tight truncate" data-testid="bilag-title">{title}</h1>
          <p className="text-muted-foreground text-sm mt-1 truncate">{subtitle || (f.counterparty ? att.filename : "Ingen dato eller nummer lest ennå")}</p>
        </div>
        <div className="text-left md:text-right shrink-0">
          <p className="font-heading text-3xl font-semibold tnum" data-testid="bilag-amount">{f.amount != null ? formatKr(f.amount, f.currency) : "–"}</p>
          <p className="text-xs text-muted-foreground">{f.vat_amount != null ? `herav mva ${formatKr(f.vat_amount, f.currency)}` : "Beløp ikke lest"}</p>
        </div>
      </div>

      {isDesktop ? (
        <ResizablePanelGroup direction="horizontal" className="min-h-[640px] rounded-xl">
          <ResizablePanel defaultSize={55} minSize={30}><div className="h-full pr-3"><AttachmentViewer attachment={att} /></div></ResizablePanel>
          <ResizableHandle withHandle />
          <ResizablePanel defaultSize={45} minSize={30}><div className="h-full pl-3 overflow-auto">{DetailsPanel}</div></ResizablePanel>
        </ResizablePanelGroup>
      ) : (
      <div>
        <Tabs defaultValue="detaljer">
          <TabsList className="w-full grid grid-cols-2 bg-card border">
            <TabsTrigger value="detaljer" data-testid="bilag-mobile-tab-details">Detaljer</TabsTrigger>
            <TabsTrigger value="dokument" data-testid="bilag-mobile-tab-document">Dokument</TabsTrigger>
          </TabsList>
          <TabsContent value="detaljer" className="mt-4">{DetailsPanel}</TabsContent>
          <TabsContent value="dokument" className="mt-4"><div className="h-[70vh]"><AttachmentViewer attachment={att} /></div></TabsContent>
        </Tabs>
        {canWrite && !isConfirmed ? (
          <div className="fixed bottom-0 left-0 right-0 z-30 border-t bg-card/95 backdrop-blur px-4 py-3 flex items-center gap-2 sm:hidden" data-testid="bilag-mobile-action-bar">
            <div className="min-w-0 flex-1">
              <p className="text-xs text-muted-foreground truncate">{title}</p>
              <p className="font-semibold tnum whitespace-nowrap">{f.amount != null ? formatKr(f.amount, f.currency) : "–"}</p>
            </div>
            {isNew && !editing ? (
              <Button onClick={analyze} disabled={!!busy} data-testid="bilag-mobile-analyze"><Sparkles className="h-4 w-4 mr-2" /> Les med AI</Button>
            ) : (
              <Button onClick={() => save(true)} disabled={saving || !!busy || !form.doc_type} data-testid="bilag-mobile-confirm"><CheckCircle2 className="h-4 w-4 mr-2" /> Bekreft</Button>
            )}
          </div>
        ) : null}
      </div>
      )}

      <LinkPickerDialog open={picker === "sales"} onOpenChange={(o) => setPicker(o ? "sales" : null)} kind="sales" initialQuery={f.counterparty || ""} onPick={(it) => link("sales", it)} busy={busy === "link"} />
      <LinkPickerDialog open={picker === "invoice"} onOpenChange={(o) => setPicker(o ? "invoice" : null)} kind="invoice" initialQuery={f.counterparty || ""} onPick={(it) => link("invoice", it)} busy={busy === "link"} />

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="bg-card">
          <AlertDialogHeader>
            <AlertDialogTitle>Slette bilaget?</AlertDialogTitle>
            <AlertDialogDescription>
              Bilaget fjernes fra bilagsbanken{linkedInvoice ? ", men regningen i Regninger beholdes med dokumentet sitt" : " og filen slettes permanent"}. Dette kan ikke angres.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="bilag-delete-cancel">Avbryt</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={remove} data-testid="bilag-delete-confirm">Slett</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
